import { fetchActivity, fetchAssignedActivity, createActivity, updateActivity, assignActivityKpi, removeActivityKpi, enableActivity, disableActivity } from "@features/admin/activity/api/activityApi";
import { ActivityResponse } from "@features/admin/activity/types/activity";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse } from "@core/types/apiMessageResponse";

/**
 * Fetches activity information for a given nation identifier.
 *
 * This async wrapper delegates to `fetchActivity` and returns the API response
 * containing activity data and any accompanying metadata (e.g., pagination).
 *
 * @param nationId - The numeric nation identifier for the subject whose activity is being requested.
 * @param params - Optional request parameters (filters, pagination, sorting, etc.) passed through to the underlying API call.
 * @returns A promise that resolves to an `ApiResponse<ActivityResponse>` containing the fetched activity data.
 *
 * @throws Errors thrown by the underlying `fetchActivity` call (network errors, non-2xx responses, etc.).
 *
 */
export const fetchActivityAsync = async (nationId: number, params: any): Promise<ApiResponse<ActivityResponse>> => {
    const response = await fetchActivity(nationId, params);
    return response;
};

export const fetchAssignedActivityAsync = async (nationId: number, departmentId: number, templateId: number, params: any): Promise<ApiResponse<ActivityResponse>> => {
    const response = await fetchAssignedActivity(nationId, departmentId, templateId, params);
    return response;
};

/**
 * Create a new activity for the specified nation identifier.
 *
 * Calls the underlying `createActivity` service with the provided payload and
 * returns the resulting API response.
 *
 * @param nationId - The numeric nation identifier for which the activity will be created.
 * @param data - The payload containing activity details (shape depends on the API).
 * @returns A promise that resolves to an ApiResponse containing an ActivityResponse.
 *
 * @throws Propagates errors thrown by the `createActivity` call (e.g., network or validation errors).
 *
 */
export const createActivityAsync = async (nationId: number, data: any): Promise<ApiResponse<any>> => {
    const response = await createActivity(nationId, data);
    return response;
};

/**
 * Updates an activity for a given nation identifier and activity id.
 *
 * Calls the underlying updateActivity service and returns its ApiResponse.
 *
 * @param nationId - Unique nation identifier for the entity that owns the activity.
 * @param activityId - Identifier of the activity to update.
 * @param data - Payload containing fields to update on the activity.
 * @returns A promise that resolves to ApiResponse<ActivityResponse> containing the updated activity data or error information.
 * @throws Propagates errors thrown by the underlying updateActivity call (e.g., network or API errors).
 *
 */
export const updateActivityAsync = async (nationId: number, activityId: number, data: any): Promise<ApiResponse<any>> => {
    const response = await updateActivity(nationId, activityId, data);
    return response;
};

/**
 * Assigns KPI data to a specific activity for the provided nation identifier by calling the underlying API helper.
 *
 * @param nationId - Numeric nation identifier for the entity (user/organization) owning the activity.
 * @param activityId - Identifier of the activity to which the KPI should be assigned.
 * @param data - Payload containing KPI assignment details (shape expected by the backend).
 * @returns A promise that resolves to an ApiResponse<ActivityResponse> containing the API result and updated activity information.
 * @throws Propagates errors from the underlying assignActivityKpi call (e.g., network or validation errors).
 * 
 */
export const assignActivityKpiAsync = async (nationId: number, activityId: number, data: any): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await assignActivityKpi(nationId, activityId, data);
    return response;
};

export const removeActivityKpiAsync = async (
    nationId: number,
    activityId: number,
    data: { kpi_ids: number[] }
): Promise<ApiResponse<any>> => {
    const response = await removeActivityKpi(nationId, activityId, data);
    return response;
};

/** Disables an activity (sets it inactive). */
export const disableActivityAsync = async (nationId: number, activityId: number): Promise<ApiResponse<any>> => {
    return disableActivity(nationId, activityId);
};

/** Enables an activity (sets it active). */
export const enableActivityAsync = async (nationId: number, activityId: number): Promise<ApiResponse<any>> => {
    return enableActivity(nationId, activityId);
};